import React, { CSSProperties } from 'react';

export type DragStartInfo = {
  startX: number;
  startY: number;
  event: React.PointerEvent<HTMLElement>;
};

export type DragMoveInfo = {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  relativeX: number;
  relativeY: number;
  movementX: number;
  movementY: number;
  event: React.PointerEvent<HTMLElement>;
};

export type DragEndInfo = {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  relativeX: number;
  relativeY: number;
  event: React.PointerEvent<HTMLElement>;
};

export type StartDragFunction = (info: DragStartInfo) => PendingDragActions | undefined;
export type PendingDragActions = {
  move: (info: DragMoveInfo) => void;
  finish?: (info: DragEndInfo) => void;
  cancel?: (info: Omit<DragEndInfo, 'event'> & Partial<DragEndInfo>) => void;
};

type PendingDragState = {
  actions: PendingDragActions;
  pointerId: number;
  pointerCapturedBy: Element | null;
  startX: number;
  startY: number;
  restoreCursorStyle?: () => void;
};

export function usePointerDrag({
  startDrag,
  dragHandleSelector,
  allowDraggingByRootElement = true,
  minimalDragDistance = 0,
  setCursor,
}: {
  startDrag?: StartDragFunction;
  dragHandleSelector?: string;
  allowDraggingByRootElement?: boolean;
  minimalDragDistance?: number;
  setCursor?: CSSProperties['cursor'];
}) {
  const pendingDrag = React.useRef<PendingDragState>();

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!startDrag || pendingDrag.current || event.buttons !== 1) {
        return;
      }

      const isHandle =
        (allowDraggingByRootElement && event.currentTarget === event.target) ||
        (dragHandleSelector &&
          event.target instanceof HTMLElement &&
          event.target.matches(dragHandleSelector));

      if (!isHandle) {
        return;
      }

      const actions = startDrag({
        startX: event.clientX,
        startY: event.clientY,
        event,
      });

      if (!actions) {
        return;
      }

      const { currentTarget } = event;
      const previousCursorStyle = currentTarget.style.cursor;
      const restoreCursorStyle = setCursor
        ? () => {
            currentTarget.style.cursor = previousCursorStyle;
          }
        : undefined;

      if (setCursor) {
        currentTarget.style.cursor = setCursor;
      }

      pendingDrag.current = {
        actions,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        restoreCursorStyle,
        pointerCapturedBy: minimalDragDistance ? null : event.currentTarget,
      };

      if (!minimalDragDistance) {
        event.currentTarget.setPointerCapture(event.pointerId);

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [startDrag, allowDraggingByRootElement, dragHandleSelector, setCursor, minimalDragDistance],
  );

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (pendingDrag.current?.pointerId !== event.pointerId) {
        return;
      }

      const { clientX, clientY, movementX, movementY } = event;

      const relativeX = clientX - pendingDrag.current.startX;
      const relativeY = clientY - pendingDrag.current.startY;

      if (
        !pendingDrag.current?.pointerCapturedBy &&
        Math.hypot(relativeX, relativeY) < minimalDragDistance
      ) {
        return;
      }

      if (!pendingDrag.current?.pointerCapturedBy) {
        event.currentTarget.setPointerCapture(event.pointerId);
        pendingDrag.current.pointerCapturedBy = event.currentTarget;
      }

      event.stopPropagation();
      pendingDrag.current.actions.move({
        startX: pendingDrag.current.startX,
        startY: pendingDrag.current.startY,
        currentX: clientX,
        currentY: clientY,
        relativeX,
        relativeY,
        movementX,
        movementY,
        event,
      });
    },
    [minimalDragDistance],
  );

  const onPointerLeave = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (pendingDrag.current?.pointerId !== event.pointerId) {
        return;
      }

      // In case the pointer is leaving the element, we'd lose the future move events and couldn't determine
      // if minimalDragDistance threshold is reached - so we assume that if you drag out of the element, you
      // want the drag to happen.
      // Note: in case minimalDragDistance === 0, onPointerLeave never happens while in pendingDrag state.
      if (!pendingDrag.current?.pointerCapturedBy) {
        event.currentTarget.setPointerCapture(event.pointerId);
        pendingDrag.current.pointerCapturedBy = event.currentTarget;
      }

      onPointerMove(event);
    },
    [onPointerMove],
  );

  const onDragEndingEvent = React.useCallback((event: React.PointerEvent<HTMLElement>) => {
    const dragState = pendingDrag.current;

    if (dragState?.pointerId !== event.pointerId || (event.buttons & 1) !== 0) {
      return;
    }

    dragState.restoreCursorStyle?.();
    event.currentTarget.releasePointerCapture(event.pointerId);
    pendingDrag.current = undefined;

    const info = {
      startX: dragState.startX,
      startY: dragState.startY,
      currentX: event.clientX,
      currentY: event.clientY,
      relativeX: event.clientX - dragState.startX,
      relativeY: event.clientY - dragState.startY,
      event,
    };

    if (event.type === 'pointercancel') {
      dragState.actions.cancel?.(info);
    } else {
      // Treat lostpointercapture the same as pointerup. Apparently this is
      // the right approach.
      //
      // We had some bug that was fixed this way in the past. Unfortunately,
      // it was documented in a Jira comment which was lost and I don't know
      // and details of the issue.
      dragState.actions.finish?.(info);
    }
  }, []);

  React.useEffect(() => {
    return () => {
      const dragState = pendingDrag.current;

      if (dragState) {
        dragState.restoreCursorStyle?.();
        dragState.actions.cancel?.({
          startX: dragState.startX,
          startY: dragState.startY,
          currentX: NaN,
          currentY: NaN,
          relativeX: NaN,
          relativeY: NaN,
          event: undefined,
        });
        dragState.pointerCapturedBy?.releasePointerCapture(dragState.pointerId);

        pendingDrag.current = undefined;
      }
    };
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerLeave,
    onPointerUp: onDragEndingEvent,
    onPointerCancel: onDragEndingEvent,
    onLostPointerCapture: onDragEndingEvent,
  };
}
